import { createContext, useContext, useState, useEffect, useMemo, useCallback } from 'react'
import type { ReactNode } from 'react'
import { useProfile } from '../hooks/useProfile'
import { getPortfolios, getPortfolioPropertyIds } from '../services/portfolios'
import { useTeamContext } from './TeamContext'
import type { Portfolio } from '../types/common'

interface PortfolioContextValue {
  selectedPortfolioId: string | null
  selectedPortfolio: Portfolio | null
  portfolios: Portfolio[]
  setSelectedPortfolioId: (id: string | null) => void
  portfolioPropertyIds: string[] | null // null = no filter (all properties)
  isAgent: boolean
  loading: boolean
  refreshPortfolios: () => Promise<void>
}

const PortfolioContext = createContext<PortfolioContextValue>({
  selectedPortfolioId: null,
  selectedPortfolio: null,
  portfolios: [],
  setSelectedPortfolioId: () => {},
  portfolioPropertyIds: null,
  isAgent: false,
  loading: false,
  refreshPortfolios: async () => {},
})

export function PortfolioProvider({ children }: { children: ReactNode }) {
  const { profile, loading: profileLoading } = useProfile()
  const { isTeamMember, agentId, portfolioIds: teamPortfolioIds, loading: teamLoading } = useTeamContext()
  const isAgent = profile?.role === 'agent'

  const [portfolios, setPortfolios] = useState<Portfolio[]>([])
  const [selectedPortfolioId, setSelectedPortfolioId] = useState<string | null>(null)
  const [portfolioPropertyIds, setPortfolioPropertyIds] = useState<string[] | null>(null)
  const [loading, setLoading] = useState(false)

  const selectedPortfolio = useMemo(
    () => portfolios.find(p => p.id === selectedPortfolioId) ?? null,
    [portfolios, selectedPortfolioId]
  )

  const refreshPortfolios = useCallback(async () => {
    if (!isAgent) return
    try {
      setLoading(true)
      if (isTeamMember && agentId) {
        // Team member: load the AGENT's portfolios, filter to accessible set
        const data = await getPortfolios(false, agentId)
        const filtered = teamPortfolioIds.length > 0
          ? data.filter(p => teamPortfolioIds.includes(p.id))
          : data // admin with all portfolios
        setPortfolios(filtered)
      } else {
        // Owner: unchanged
        const data = await getPortfolios(false)
        setPortfolios(data)
      }
    } catch {
      // Silently fail — portfolio list is non-critical
    } finally {
      setLoading(false)
    }
  }, [isAgent, isTeamMember, agentId, teamPortfolioIds])

  // Load portfolios when agent profile is ready AND team context resolved
  useEffect(() => {
    // Gate: wait for team context to resolve before loading portfolios.
    // For owners this is one fast round-trip (team query returns null), then normal load.
    if (teamLoading) return
    if (!profileLoading && isAgent) {
      refreshPortfolios()
    }
  }, [profileLoading, isAgent, teamLoading, refreshPortfolios])

  // Load property IDs when a portfolio is selected
  useEffect(() => {
    if (!selectedPortfolioId) {
      setPortfolioPropertyIds(null) // null = no filter (RLS scopes to accessible set)
      return
    }

    let cancelled = false
    async function loadPropertyIds() {
      try {
        // Team member: pass agentId to skip the owner_id=userId ownership check
        // (RLS on portfolio_properties already enforces team access)
        const ids = await getPortfolioPropertyIds(selectedPortfolioId!, isTeamMember && agentId ? agentId : undefined)
        if (!cancelled) {
          setPortfolioPropertyIds(ids)
        }
      } catch {
        if (!cancelled) {
          setPortfolioPropertyIds(null)
        }
      }
    }
    loadPropertyIds()

    return () => { cancelled = true }
  }, [selectedPortfolioId, isTeamMember, agentId])

  const value = useMemo<PortfolioContextValue>(() => ({
    selectedPortfolioId,
    selectedPortfolio,
    portfolios,
    setSelectedPortfolioId,
    portfolioPropertyIds,
    isAgent,
    loading,
    refreshPortfolios,
  }), [selectedPortfolioId, selectedPortfolio, portfolios, portfolioPropertyIds, isAgent, loading, refreshPortfolios])

  return (
    <PortfolioContext.Provider value={value}>
      {children}
    </PortfolioContext.Provider>
  )
}

export function usePortfolioContext() {
  return useContext(PortfolioContext)
}
